import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { HomeLogoComponent } from './home-logo/home-logo.component';
import { IconComponent } from '@shared/ui-components/icon/icon.component';
import { NavLinksComponent } from './nav-links/nav-links.component';
import { LangSelectionComponent } from './lang-selection/lang-selection.component';
import { IconButtonComponent } from '@shared/ui-components/icon-button/icon-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationsTriggerComponent } from '@core/components/header/notifications-trigger/notifications-trigger.component';
import { ProfileTriggerComponent } from '@core/components/header/profile-trigger/profile-trigger.component';
import { HelpTriggerComponent } from './help-trigger/help-trigger.component';
import { featureFlags } from '@shared/constants/feature-flags';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { userSelectors } from '@store/selectors/user.selectors';
import { filter, map, startWith } from 'rxjs';

@Component({
  selector: 'ac-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HomeLogoComponent,
    IconComponent,
    NavLinksComponent,
    LangSelectionComponent,
    IconButtonComponent,
    NotificationsTriggerComponent,
    TranslateModule,
    ProfileTriggerComponent,
    HelpTriggerComponent,
  ],
})
export class HeaderComponent {
  @Output() menuTrigger = new EventEmitter<void>();
  protected readonly _shouldRenderLangSelection: boolean = featureFlags.translationFeatureFlag;
  protected readonly _authUserSig = inject(Store<StoreState>).selectSignal(userSelectors.authUser);
  protected readonly _isEnergyRehabilitationModeSig = inject(Store<StoreState>).selectSignal(userSelectors.isEnergyRehabilitationMode);
  protected readonly _router = inject(Router);
  protected readonly _currentUrlSig = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this._router.url),
      startWith(this._router.url)
    ),
    { initialValue: this._router.url }
  );

  protected readonly _isRehabilitationContextSig = computed(() => {
    const currentUrlTree = this._router.parseUrl(this._currentUrlSig());
    const currentPath = currentUrlTree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '';
    const isRehabilitationFromQuery = currentUrlTree.queryParams['rehab'] === 'true';
    const isRehabilitationRoute = currentPath === 'consumption-invoices' || currentPath.startsWith('consumption-invoices/');

    return this._isEnergyRehabilitationModeSig() || isRehabilitationRoute || isRehabilitationFromQuery;
  });
}
